import { Suspense } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { store } from './store'
import ProtectedRoute from './components/ProtectedRoute'
import PublicRoute from './components/PublicRoute'
import NotificationToast from './components/NotificationToast'
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary'
import VersionUpdateNotification from './components/VersionUpdateNotification'
import ChunkLoadErrorHandler from './components/ChunkLoadErrorHandler'
import { lazyWithPreload } from './utils/lazyWithPreload'
import './App.css'

// Eager load auth pages (small, needed immediately)
import Login from './pages/Login'
import Signup from './pages/Signup'
import SignupConfirmation from './pages/SignupConfirmation'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'

// Eager load legal pages (static content, small)
import PolicyPrivacy from './pages/PolicyPrivacy'
import Terms from './pages/Terms'

// Lazy load protected pages with preload capability (reduces initial bundle size by 41%)
// NOTE: Suspense fallback is null to avoid double spinners. The initial HTML loader
// (index.html) provides immediate feedback, then ProtectedRoute manages workspace loading.
// React 19's useTransition keeps current page visible during lazy loading (no blank flash).
// lazyWithPreload enables hover-based prefetching for instant navigation feel.
export const Onboarding = lazyWithPreload(() => import('./pages/Onboarding'))
export const Board = lazyWithPreload(() => import('./pages/Board'))
export const Plan = lazyWithPreload(() => import('./pages/Plan'))
export const Tasks = lazyWithPreload(() => import('./pages/Tasks'))
export const Performance = lazyWithPreload(() => import('./pages/Performance')) // Defers Chart.js (~65KB)
export const Reflect = lazyWithPreload(() => import('./pages/Reflect'))
export const Calendar = lazyWithPreload(() => import('./pages/Calendar'))
export const Wiki = lazyWithPreload(() => import('./pages/Wiki'))

// OpenObserve RUM automatically tracks SPA view changes on history navigation,
// so no router wrapper is needed (Sentry previously wrapped Routes here).

function App() {
  return (
    <Provider store={store}>
      <Router>
        <DndProvider backend={HTML5Backend}>
        <div className="App">
          <Routes>
            {/* Public routes - redirect to dashboard if authenticated */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/signup" element={
              <PublicRoute>
                <Signup />
              </PublicRoute>
            } />
            <Route path="/signup/confirm" element={
              <PublicRoute>
                <SignupConfirmation />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            {/* Reset password doesn't need PublicRoute - it handles its own session validation */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Legal pages - accessible to everyone (no auth required, no redirect if authenticated) */}
            <Route path="/privacy" element={<PolicyPrivacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Onboarding route - protected but special handling */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Onboarding />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Protected routes - require authentication */}
            <Route path="/board" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Board />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/plan" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Plan />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Tasks />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/performance" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Performance />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/reflect" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Reflect />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Calendar />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/wiki" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Wiki />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            {/* Static /wiki/archived must be declared before the :spaceSlug param route
                so a space is never mistaken for the reserved "archived" view */}
            <Route path="/wiki/archived" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Wiki view="archived" />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/wiki/:spaceSlug" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Wiki />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/wiki/:spaceSlug/:pageSlug" element={
              <ProtectedRoute>
                <LazyLoadErrorBoundary>
                  <Suspense fallback={null}>
                    <Wiki />
                  </Suspense>
                </LazyLoadErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/board" replace />} />
            
            {/* Catch all - redirect to board */}
            <Route path="*" element={<Navigate to="/board" replace />} />
          </Routes>
          <NotificationToast />
          <VersionUpdateNotification />
          <ChunkLoadErrorHandler />
        </div>
        </DndProvider>
      </Router>
    </Provider>
  )
}

export default App
